import React, { useCallback } from "react"
import type { UVInsertFaceElement } from "@shared/models/uv-inserts"
import { getUvInsertTextPlaceholderColor } from "@shared/view/uv-inserts/get-text-placeholder-color"
import { Visualiser, VisualiserGroup, VisualiserImageArea, VisualiserTextBox } from "../components/Visualiser"
import { buildTextSize, str } from "./devFormat"

type DevFaceVisualisersProps = {
  facesToShow: UVInsertFaceElement[]
  selectedThemeImageSrc: string | null
  textByKey: Record<string, string>
  setText: (key: string, value: string) => void
  imageByKey: Record<string, File | null>
  setImageTransformByKey: React.Dispatch<
    React.SetStateAction<Record<string, { scale: number; x: number; y: number }>>
  >
  imageTransformByKey: Record<string, { scale: number; x: number; y: number }>
  /** The config's `marketSegment` — MENS inserts on teal faces get a white placeholder. */
  marketSegment?: string[]
}

/** Renders config-driven faces (Visualiser + FaceTabs already chose which faces appear). */
export function DevFaceVisualisers({
  facesToShow,
  selectedThemeImageSrc,
  textByKey,
  setText,
  imageByKey,
  setImageTransformByKey,
  imageTransformByKey,
  marketSegment,
}: DevFaceVisualisersProps) {
  const renderFace = useCallback(
    (face: UVInsertFaceElement, fi: number) => {
      /** Selected variant image on FRONT when present (single- or multi-theme). */
      const frontThemeBackground =
        face.location === "FRONT" && selectedThemeImageSrc ? selectedThemeImageSrc : null

      const placeholderColor = getUvInsertTextPlaceholderColor({
        marketSegment: marketSegment ?? [],
        faceBackgroundColor: face.backgroundColor,
      })

      return (
        <Visualiser
          key={`${face.location}-${fi}`}
          type={face.type}
          background={face.backgroundColor}
          backgroundImageUrl={frontThemeBackground ?? undefined}
          subtitle={face.subtitle}
        >
          {face.elementConfig.map((el, ei) => {
            if (el.type === "TEXT") {
              const key = el.lineItemPropertyKey?.trim() ?? ""
              if (el.editable === false) {
                if (!key) {
                  return (
                    <VisualiserTextBox
                      key={`static-${face.location}-${fi}-${ei}`}
                      font={el.font}
                      fontWeight={el.fontWeight}
                      fontSize={el.fontSize}
                      lineHeight={el.lineHeight}
                      color={el.color}
                      maxLength={el.maxLength}
                      maxLines={el.maxLines}
                      size={buildTextSize(el.size)}
                      position={{
                        top: str(el.positioning.top),
                        left: str(el.positioning.left),
                        right: str(el.positioning.right),
                        bottom: str(el.positioning.bottom),
                      }}
                      style={el.style as React.CSSProperties}
                      userEditable={false}
                      value={el.value ?? ""}
                      setValue={() => {}}
                    />
                  )
                }
                const storedReadOnly = textByKey[key]
                const readOnlyDisplay =
                  storedReadOnly != null && storedReadOnly !== ""
                    ? storedReadOnly
                    : (el.value ?? "")
                return (
                  <VisualiserTextBox
                    key={`${key}-${ei}`}
                    font={el.font}
                    fontWeight={el.fontWeight}
                    fontSize={el.fontSize}
                    lineHeight={el.lineHeight}
                    color={el.color}
                    maxLength={el.maxLength}
                    maxLines={el.maxLines}
                    size={buildTextSize(el.size)}
                    position={{
                      top: str(el.positioning.top),
                      left: str(el.positioning.left),
                      right: str(el.positioning.right),
                      bottom: str(el.positioning.bottom),
                    }}
                    style={el.style as React.CSSProperties}
                    userEditable={false}
                    value={readOnlyDisplay}
                    setValue={(v) => setText(key, v)}
                  />
                )
              }
              return (
                <VisualiserTextBox
                  key={`${key}-${ei}`}
                  font={el.font}
                  fontWeight={el.fontWeight}
                  fontSize={el.fontSize}
                  lineHeight={el.lineHeight}
                  color={el.color}
                  maxLength={el.maxLength}
                  maxLines={el.maxLines}
                  size={buildTextSize(el.size)}
                  position={{
                    top: str(el.positioning.top),
                    left: str(el.positioning.left),
                    right: str(el.positioning.right),
                    bottom: str(el.positioning.bottom),
                  }}
                  style={el.style as React.CSSProperties}
                  value={textByKey[key] ?? ""}
                  placeholderColor={placeholderColor}
                  setValue={(v) => setText(key, v)}
                />
              )
            }
            if (el.type === "IMAGE_UPLOAD") {
              const key = el.lineItemPropertyKey
              const file = imageByKey[key] ?? null
              const t = imageTransformByKey[key] ?? { scale: 1, x: 0, y: 0 }
              return (
                <VisualiserImageArea
                  key={`${key}-${ei}`}
                  file={file}
                  backgroundColor={el.backgroundColor}
                  size={{
                    width: str(el.size.width) ?? "200px",
                    height: str(el.size.height) ?? "200px",
                  }}
                  position={{
                    top: str(el.positioning.top),
                    left: str(el.positioning.left),
                  }}
                  borderRadius={el.borderRadius}
                  style={el.style as React.CSSProperties}
                  scale={t.scale}
                  x={t.x}
                  y={t.y}
                  onUpdate={(updates) => {
                    setImageTransformByKey((p) => ({
                      ...p,
                      [key]: {
                        scale: updates.scale ?? t.scale,
                        x: updates.x ?? t.x,
                        y: updates.y ?? t.y,
                      },
                    }))
                  }}
                />
              )
            }
            return null
          })}
        </Visualiser>
      )
    },
    [
      imageByKey,
      imageTransformByKey,
      marketSegment,
      selectedThemeImageSrc,
      setImageTransformByKey,
      setText,
      textByKey,
    ],
  )

  if (facesToShow.length === 0) {
    return <p className="dev-ui__muted">No faces for this region in Liquid config.</p>
  }

  return <VisualiserGroup>{facesToShow.map((face, fi) => renderFace(face, fi))}</VisualiserGroup>
}
